import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  limit as fbLimit,
  serverTimestamp,
  setDoc,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

/**
 * Insight / Idea moderation hook (admin 第 19 弾、Phase 5.5 REQ-4.3.42 / GAP-A13 MUST)。
 *
 * 旧 v1 では Insight / Idea のモデレーションは `EcwModerationPage.tsx` 等で実装されていたが、
 * 本リポでは Sprint X5c minimum で `hidden: boolean` フィールドを schema に追加済 (user 側 `useEcwInsights` /
 * `useEcwIdeas` で `hidden === true` を client-side filter)。本 sprint はこの `hidden` toggle +
 * 物理削除を admin app に開放する。
 *
 * 設計判断 (visibility vs archivedAt vs hidden):
 *   - posts は 3 段階 visibility (public / hidden_others / hidden_all) を運用するが、
 *     Insight / Idea は 公開 / 非公開 の 2 段階のみで十分 (REQ-4.3.42 にも 3 段階要件なし)
 *   - schema 上 `archivedAt: null` も持つが、user 側 hook はこれを参照していない (X5c 当初の
 *     reserved field 扱い)。一方 `hidden` は user 側 hook で実際に filter 済 → admin から toggle して
 *     即座に user 表示に反映できる
 *   - よって本 sprint は `hidden` boolean を使う。archive 機能 (将来 admin 用閲覧履歴) は別 sprint
 *   - 削除は posts-moderation 同様 物理削除 (rules で district admin / master のみ許可)
 *
 * 認可: firestore.rules
 *   - read: canAccessDistrict(districtId) → admin もこの claim を持つ (master は全地区、district は担当地区)
 *   - update hidden: isDistrictAdmin(districtId) (admin 第 19 弾で rules 拡張、master 単独だった条件を拡張)
 *   - delete: isDistrictAdmin(districtId) (同上)
 *
 * subscribe limit: 200 件 (運用初期は問題ない規模、pagination は別 sprint)。
 */

const SUBSCRIBE_LIMIT = 200;

export interface InsightModerationItem {
  id: string;
  districtId: string;
  userId: string;
  nickname: string;
  title: string;
  observations: string;
  diagnosis: string;
  sentiment: number;
  selectedPostIds: string[];
  hidden: boolean;
  createdAt: Date | null;
  /** AW10 Medium: 感情タグ (old2 EcwInsightCard.emotionTag、未設定は空文字)。 */
  emotionTag: string;
  /** AW10 Medium: カテゴリ表示名 (old2 categoryLabel)。 */
  categoryLabel: string;
  /** AW10 Medium: AI 分析テキスト (old2 llmAnalysis、行展開で全文表示)。 */
  llmAnalysis: string;
  /** AW10 Low: チーム名 (old2 team、メタ行に表示)。 */
  team: string;
}

export interface IdeaModerationItem {
  id: string;
  districtId: string;
  userId: string;
  nickname: string;
  title: string;
  observations: string;
  actionIdea: string;
  insightIds: string[];
  selectedPostIds: string[];
  hidden: boolean;
  createdAt: Date | null;
  /** AW10 Medium: 感情タグ (old2 EcwIdeaCard.emotionTag)。 */
  emotionTag: string;
  /** AW10 Medium: カテゴリ表示名 (old2 categoryLabel)。 */
  categoryLabel: string;
  /** AW10 Medium: AI 分析テキスト (old2 llmAnalysis)。 */
  llmAnalysis: string;
  /** AW10 Low: チーム名 (old2 team)。 */
  team: string;
}

export type InsightsModerationState =
  | { status: 'loading'; insights: []; ideas: []; error: null }
  | {
      status: 'ready';
      insights: InsightModerationItem[];
      ideas: IdeaModerationItem[];
      error: null;
    }
  | { status: 'error'; insights: []; ideas: []; error: Error };

function tsToDate(value: unknown): Date | null {
  if (!value) return null;
  if (typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate();
    } catch {
      return null;
    }
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** Firestore doc → InsightModerationItem (typeof narrow で Firestore 異常値を defensive に弾く)。 */
export function mapInsightDoc(
  snap: { id: string; data: () => DocumentData },
  fallbackDistrictId: string,
): InsightModerationItem {
  const data = snap.data();
  return {
    id: snap.id,
    districtId: typeof data.districtId === 'string' ? data.districtId : fallbackDistrictId,
    userId: typeof data.userId === 'string' ? data.userId : '',
    nickname: typeof data.nickname === 'string' ? data.nickname : '(匿名)',
    title: typeof data.title === 'string' ? data.title : '',
    observations: typeof data.observations === 'string' ? data.observations : '',
    diagnosis: typeof data.diagnosis === 'string' ? data.diagnosis : '',
    sentiment: typeof data.sentiment === 'number' ? data.sentiment : 0,
    selectedPostIds: Array.isArray(data.selectedPostIds)
      ? data.selectedPostIds.filter((v): v is string => typeof v === 'string')
      : [],
    hidden: data.hidden === true,
    createdAt: tsToDate(data.createdAt),
    emotionTag: typeof data.emotionTag === 'string' ? data.emotionTag : '',
    categoryLabel: typeof data.categoryLabel === 'string' ? data.categoryLabel : '',
    llmAnalysis: typeof data.llmAnalysis === 'string' ? data.llmAnalysis : '',
    team: typeof data.team === 'string' ? data.team : '',
  };
}

/** Firestore doc → IdeaModerationItem (typeof narrow で defensive)。 */
export function mapIdeaDoc(
  snap: { id: string; data: () => DocumentData },
  fallbackDistrictId: string,
): IdeaModerationItem {
  const data = snap.data();
  return {
    id: snap.id,
    districtId: typeof data.districtId === 'string' ? data.districtId : fallbackDistrictId,
    userId: typeof data.userId === 'string' ? data.userId : '',
    nickname: typeof data.nickname === 'string' ? data.nickname : '(匿名)',
    title: typeof data.title === 'string' ? data.title : '',
    observations: typeof data.observations === 'string' ? data.observations : '',
    actionIdea: typeof data.actionIdea === 'string' ? data.actionIdea : '',
    insightIds: Array.isArray(data.insightIds)
      ? data.insightIds.filter((v): v is string => typeof v === 'string')
      : [],
    selectedPostIds: Array.isArray(data.selectedPostIds)
      ? data.selectedPostIds.filter((v): v is string => typeof v === 'string')
      : [],
    hidden: data.hidden === true,
    createdAt: tsToDate(data.createdAt),
    emotionTag: typeof data.emotionTag === 'string' ? data.emotionTag : '',
    categoryLabel: typeof data.categoryLabel === 'string' ? data.categoryLabel : '',
    llmAnalysis: typeof data.llmAnalysis === 'string' ? data.llmAnalysis : '',
    team: typeof data.team === 'string' ? data.team : '',
  };
}

/**
 * Insight / Idea を 1 hook で同時 subscribe (両 collection の read 完了後 ready)。
 *
 * 旧との差分: hidden=true 含めて全件返す (admin の判断材料、user 側 hook は hidden=true を filter 済)。
 */
export function useInsightsModerationList(districtId: string | null): InsightsModerationState {
  const [state, setState] = useState<InsightsModerationState>({
    status: 'loading',
    insights: [],
    ideas: [],
    error: null,
  });

  useEffect(() => {
    if (!districtId) {
      setState({
        status: 'error',
        insights: [],
        ideas: [],
        error: new Error('districtId is required'),
      });
      return;
    }

    // 二つの subscribe を持ち、両方 ready で初めて status=ready にする。
    // 部分更新中に loading に戻さない (UX 上の flicker 防止) ため、最新値はクロージャに保持。
    let insightsReady = false;
    let ideasReady = false;
    let latestInsights: InsightModerationItem[] = [];
    let latestIdeas: IdeaModerationItem[] = [];

    function maybeEmit(): void {
      if (insightsReady && ideasReady) {
        setState({
          status: 'ready',
          insights: latestInsights,
          ideas: latestIdeas,
          error: null,
        });
      }
    }

    const qInsights = query(
      collection(db, 'districts', districtId, 'ecwInsights'),
      orderBy('createdAt', 'desc'),
      fbLimit(SUBSCRIBE_LIMIT),
    );
    const qIdeas = query(
      collection(db, 'districts', districtId, 'ecwIdeas'),
      orderBy('createdAt', 'desc'),
      fbLimit(SUBSCRIBE_LIMIT),
    );

    const unsubInsights: Unsubscribe = onSnapshot(
      qInsights,
      (snap) => {
        latestInsights = snap.docs.map((d) => mapInsightDoc(d, districtId));
        insightsReady = true;
        maybeEmit();
      },
      (error) => setState({ status: 'error', insights: [], ideas: [], error }),
    );
    const unsubIdeas: Unsubscribe = onSnapshot(
      qIdeas,
      (snap) => {
        latestIdeas = snap.docs.map((d) => mapIdeaDoc(d, districtId));
        ideasReady = true;
        maybeEmit();
      },
      (error) => setState({ status: 'error', insights: [], ideas: [], error }),
    );

    return () => {
      unsubInsights();
      unsubIdeas();
    };
  }, [districtId]);

  return state;
}

/** Insight の hidden toggle (admin 専用、REQ-4.3.42)。 */
export async function setInsightHidden(
  districtId: string,
  insightId: string,
  hidden: boolean,
  uid: string,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'ecwInsights', insightId);
  await setDoc(
    ref,
    {
      hidden,
      moderatedBy: uid,
      moderatedAt: new Date().toISOString(),
      updatedAt: serverTimestamp(),
      updatedBy: uid,
    },
    { merge: true },
  );
}

/** Insight を物理削除 (admin 専用、REQ-4.3.42 完全削除)。 */
export async function deleteInsight(districtId: string, insightId: string): Promise<void> {
  await deleteDoc(doc(db, 'districts', districtId, 'ecwInsights', insightId));
}

/** Idea の hidden toggle (admin 専用、REQ-4.3.42)。 */
export async function setIdeaHidden(
  districtId: string,
  ideaId: string,
  hidden: boolean,
  uid: string,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'ecwIdeas', ideaId);
  await setDoc(
    ref,
    {
      hidden,
      moderatedBy: uid,
      moderatedAt: new Date().toISOString(),
      updatedAt: serverTimestamp(),
      updatedBy: uid,
    },
    { merge: true },
  );
}

/** Idea を物理削除 (admin 専用、REQ-4.3.42 完全削除)。 */
export async function deleteIdea(districtId: string, ideaId: string): Promise<void> {
  await deleteDoc(doc(db, 'districts', districtId, 'ecwIdeas', ideaId));
}

/** 一括操作の種別 (AW10 High、old2 EcwModerationPage.bulkAction 踏襲)。 */
export type BulkAction = 'hide' | 'show' | 'delete';

/** 一括操作対象 collection (タブと 1:1)。 */
export type ModerationCollection = 'ecwInsights' | 'ecwIdeas';

export interface BulkResult {
  ok: number;
  fail: number;
}

/**
 * 選択した複数 doc に対して一括で hide/show/delete を適用 (AW10 High、old2 bulkAction 踏襲)。
 *
 * old2 は updateDoc/deleteDoc を逐次 await していたが、v2 は Promise.allSettled で並列化し
 * 成功/失敗件数を集計して返す (呼出側で toast「N 件処理完了 (M 件失敗)」を表示)。
 * delete は物理削除、hide/show は `hidden` boolean toggle (single 版と同 audit フィールド)。
 */
export async function bulkModerate(
  districtId: string,
  collName: ModerationCollection,
  ids: readonly string[],
  action: BulkAction,
  uid: string,
): Promise<BulkResult> {
  const results = await Promise.allSettled(
    ids.map((id) => {
      const ref = doc(db, 'districts', districtId, collName, id);
      if (action === 'delete') return deleteDoc(ref);
      return setDoc(
        ref,
        {
          hidden: action === 'hide',
          moderatedBy: uid,
          moderatedAt: new Date().toISOString(),
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
    }),
  );
  let ok = 0;
  let fail = 0;
  for (const r of results) {
    if (r.status === 'fulfilled') ok += 1;
    else fail += 1;
  }
  return { ok, fail };
}
